import { Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { type NotificationItem, useNotifications } from '../../hooks/useNotifications';
import { usePushSetup } from '../../hooks/usePushSetup';
import { type PortalApp, portalNotificationPath } from '../../lib/notification-routes';
import type { PushDriver } from '../../lib/push-setup';
import { Button } from '../../ui/button';
import { NotificationList } from './NotificationList';
import { PushSetupPrompt } from './PushSetupPrompt';

const PUSH_LABEL: Record<string, string> = {
  loading: 'Checking…',
  ready: 'Desktop alerts on',
  'never-enabled': 'Enable desktop alerts',
  'device-missing': 'Register this browser',
  blocked: 'Alerts blocked by browser',
  unsupported: 'Alerts unavailable here',
};

export interface NotificationsPageProps {
  app: PortalApp;
  driver: PushDriver;
}

/**
 * The whole inbox. Both portals mount this directly rather than wrapping it —
 * there is nothing app-specific here beyond which route family a target opens
 * into, which is what `app` selects.
 */
export function NotificationsPage({ app, driver }: NotificationsPageProps) {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const { notifications, unreadCount, loading, markRead, markAllRead, dismiss } =
    useNotifications();
  const { status, actionable, enable, disable, busy, error } = usePushSetup(driver);

  const togglePush = async () => {
    if (status === 'ready') await disable();
    else await enable();
  };

  const openNotification = async (notification: NotificationItem) => {
    if (!notification.isRead) await markRead.mutateAsync({ id: notification.id });
    const path = portalNotificationPath(app, { ...notification, orgId, workspaceId });
    // Already on the inbox, so a notification with no specific target stays put.
    if (path) navigate(path);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center justify-between gap-2 border-b border-border p-4">
        <div>
          <h1 className="text-lg font-semibold">Notifications</h1>
          <p className="text-xs text-muted-foreground">
            Collaboration updates stay here even if push is unavailable.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void togglePush()}
            disabled={!actionable || busy}
          >
            {PUSH_LABEL[status]}
          </Button>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          )}
        </div>
      </header>

      {error && <p className="px-4 pt-2 text-xs text-destructive">{error}</p>}

      <PushSetupPrompt driver={driver} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <NotificationList
          notifications={notifications}
          onOpen={(notification) => void openNotification(notification)}
          onDismiss={(notification) => dismiss.mutate({ id: notification.id })}
          emptyTitle="No notifications"
          emptyDescription="You're all caught up. Check back later for updates."
        />
      </div>
    </div>
  );
}
